import { AxiosError } from 'axios';
import React from 'react';
import { Link } from 'react-aria-components';
import { useForm } from 'react-hook-form';

import { mapPaidBreakExceptionToPaidBreakExceptionFormValue } from '#/shared/adapters/mapPaidBreakExceptionToPaidBreakExceptionFormValue';
import { Button } from '#/shared/components/Button';
import { Card } from '#/shared/components/Card';
import {
  FormControlMultiTextInput,
  FormControlSignature,
  FormControlTextArea,
  FormControlTextInput,
} from '#/shared/components/FormControl';
import { FormFieldSupportingText } from '#/shared/components/FormField/FormFieldSupportingText';
import { MaterialIcon } from '#/shared/components/MaterialIcon';
import { appConfig } from '#/shared/config/AppConfig';
import { useAnalytics } from '#/shared/hooks/useAnalytics';
import { useFeatureFlag } from '#/shared/hooks/useFeatureFlag';
import { useMonitoring } from '#/shared/hooks/useMonitoring';
import { useWindowScrolling } from '#/shared/hooks/useWindowScrolling';
import { usePageStateStore } from '#/shared/stores/pageStateStore';
import { useTimesheetStore } from '#/shared/stores/timesheetStore';
import { isVisibleInViewport } from '#/shared/utils/domHelper';
import { validateEmail, validateRequiredField } from '#/shared/utils/formHelper';

import { mapTimesheetApprovalFormValuesToApproveTimesheetApiData } from '../adaptors/mapTimesheetApprovalFormValuesToApproveTimesheetApiData';
import { useApproveTimesheetMutation } from '../hooks/useApproveTimesheetMutation';
import { usePaidBreakExceptionValidation } from '../hooks/usePaidBreakExceptionValidation';
import { type ApproveTimesheetApiErrorResponseData } from '../types/ApproveTimesheetApi';
import { type TimesheetApprovalFormValues } from '../types/TimesheetApprovalFormValues';

type TimesheetApprovalProps = {
  bannerElement: React.ReactElement;
};

export function TimesheetApprovalOld({ bannerElement }: Readonly<TimesheetApprovalProps>) {
  const { noticeError } = useMonitoring();
  const { track } = useAnalytics();
  const { isEnabled: paidBreakExceptionIsEnabled } = useFeatureFlag('PPL-2119-paid-break-exception');
  const { isEnabled: signatureAsArrayIsEnabled } = useFeatureFlag('PPL-2300-signature-as-array-of-strings');

  const setPageState = usePageStateStore(state => state.setPageState);

  const timesheet = useTimesheetStore(state => state.timesheet);
  const timesheetId = timesheet?.timesheetId ?? '';
  const doctorName = timesheet?.doctorName ?? '';

  const { control, setValue, watch, setError, clearErrors, formState, trigger, getValues, register, setFocus } =
    useForm<TimesheetApprovalFormValues>({
      defaultValues: {
        name: '',
        role: '',
        department: '',
        signature: '',
        signedAt: undefined,
        signatureType: 'drawn',
        approverEmails: [{ email: '' }],
        approverNotes: undefined,
        paidBreakExceptions: [],
      },
      // We use onChange mode to trigger validation on each field change, because of the custom validation logic on submit
      mode: 'onChange',
    });

  const signedAt = watch('signedAt');
  const signatureType = watch('signatureType');

  const approveTimesheet = useApproveTimesheetMutation();
  const [isSubmissionError, setIsSubmissionError] = React.useState(false);
  const [isSubmissionPending, setIsSubmissionPending] = React.useState(false);

  const { validate: validatePaidBreakExceptions } = usePaidBreakExceptionValidation({
    isEnabled: paidBreakExceptionIsEnabled,
    setError,
    clearErrors,
    trigger,
    fieldName: 'paidBreakExceptions',
  });

  // Custom submit handler that validates paid break exceptions first
  // then triggers form validation manually and call onSubmit when valid
  const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmissionError(false);

    // Validate paid break exceptions
    const paidBreakValidation = await validatePaidBreakExceptions();

    if (paidBreakValidation.shouldBlockSubmission) {
      // Scroll to first invalid paid break exception field
      if (paidBreakValidation.invalidDates && paidBreakValidation.invalidDates.length > 0) {
        const firstInvalidDate = paidBreakValidation.invalidDates[0];
        const firstInvalidField = document.querySelector(
          `[data-paid-break-exception-field][data-shift-date="${firstInvalidDate}"]`,
        ) as HTMLElement;

        if (firstInvalidField) {
          firstInvalidField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }

      // Block submission if paid break validation fails
      return;
    }

    // Manually trigger validation for all fields
    const isFormValid = await trigger();

    if (!isFormValid) {
      // Focus the first invalid field since we are using custom validation/submission handler
      const errorFields = Object.keys(formState?.errors ?? {});
      if (errorFields.length > 0) {
        setFocus(errorFields[0] as keyof TimesheetApprovalFormValues);
      }
      return;
    }

    const formData = getValues();
    await onSubmit(formData);
  };

  const onSubmit = async (formData: TimesheetApprovalFormValues) => {
    setIsSubmissionPending(true);

    // Transform store exceptions to form values for submission and update form state
    if (paidBreakExceptionIsEnabled) {
      const paidBreakExceptions = mapPaidBreakExceptionToPaidBreakExceptionFormValue();
      formData.paidBreakExceptions = paidBreakExceptions;
      setValue('paidBreakExceptions', paidBreakExceptions);
    }

    const hasApproverEmail = formData.approverEmails?.some(value => value.email.trim() !== '') ?? false;

    const signatureTypeValue = formData.signatureType ?? 'drawn';

    try {
      const data = mapTimesheetApprovalFormValuesToApproveTimesheetApiData({
        formValues: formData,
        paidBreakExceptionIsEnabled,
        signatureAsArrayIsEnabled,
      });

      await approveTimesheet.mutateAsync({ data });

      track('Online approval web - Approval Success', {
        Timesheet_ID: timesheetId,
        Has_Approver_Email: hasApproverEmail,
        Signature: signatureTypeValue,
      });

      setIsSubmissionPending(false);
      setPageState('submission-success');
    } catch (error) {
      setIsSubmissionError(true);
      setIsSubmissionPending(false);
      track('Online approval web - Approval Failure', {
        Timesheet_ID: timesheetId,
        Has_Approver_Email: hasApproverEmail,
        Signature: signatureTypeValue,
      });
      const expectedError = error as AxiosError<ApproveTimesheetApiErrorResponseData>;
      noticeError(new Error('Timesheet online approval has failed'), {
        originalMessage: expectedError?.message,
        originalData: expectedError?.response?.data,
        originalError: expectedError,
        timesheetId,
      });
    }
  };

  // watch when the button is in the viewport or not to make it floating when it is not
  const buttonAnchorRef = React.useRef<HTMLDivElement>(null);
  const [isButtonFixed, setIsButtonFixed] = React.useState<boolean>(true);
  useWindowScrolling({ onScroll: () => setIsButtonFixed(!isVisibleInViewport(buttonAnchorRef.current)) });

  return (
    <Card className="max-tablet-portrait:pl-20 max-tablet-portrait:pr-20 max-tablet-portrait:border-0 max-tablet-portrait:rounded-none max-tablet-portrait:border-y-1">
      <div className="mb-24 hidden tablet-portrait:block">{bannerElement}</div>

      <form className="gap-32 flex flex-col" onSubmit={handleFormSubmit}>
        <div>
          <h2 className="text-24 font-700 mb-8">Approval</h2>
          <p>If the timesheet requires changes before approval, contact {doctorName}.</p>
        </div>
        <div className="gap-16 flex flex-col">
          <h3 className="text-18 font-700">Approver details</h3>

          <FormControlTextInput
            label="Full name"
            placeholder="Enter text"
            name={'name'}
            type="text"
            control={control}
            rules={{
              validate: {
                required: validateRequiredField,
              },
              maxLength: { value: 80, message: 'Maximum allowed characters is 80' },
            }}
            errorLeadingIconName={'error'}
          />

          <FormControlTextInput
            label="Position"
            placeholder="Enter text"
            name={'role'}
            type="text"
            control={control}
            rules={{
              validate: {
                required: validateRequiredField,
              },
              maxLength: { value: 80, message: 'Maximum allowed characters is 80' },
            }}
            errorLeadingIconName={'error'}
          />

          <FormControlTextInput
            label="Department (optional)"
            placeholder="Enter text"
            name={'department'}
            type="text"
            control={control}
            rules={{
              maxLength: { value: 80, message: 'Maximum allowed characters is 80' },
            }}
            errorLeadingIconName={'error'}
          />
        </div>

        <div className="gap-16 flex flex-col">
          <h3 className="text-18 font-700">Signature</h3>
          <FormControlSignature
            label={''}
            aria-label="Your signature"
            name={'signature'}
            control={control}
            rules={{ required: 'Signature required to continue' }}
            signedAt={signedAt}
            signatureType={signatureType}
            onSignAtChange={(date, type) => {
              setValue('signedAt', date);
              setValue('signatureType', type);
            }}
            errorLeadingIconName={'error'}
          />
        </div>

        <div className="gap-16 flex flex-col">
          <h3 className="text-18 font-700">Timesheet options</h3>

          <FormControlMultiTextInput
            name="approverEmails"
            control={control}
            label="Email address (optional)"
            description="If you'd like to receive a copy of the approved timesheet."
            placeholder="Enter email"
            addFieldButtonLabel="Add another email address"
            type="text"
            fieldKey="email"
            minFields={1}
            maxFields={5}
            validate={validateEmail}
            register={register}
          />

          <FormControlTextArea
            label="Notes (optional)"
            description="Included on timesheet and visible to the doctor and Medrecruit staff."
            descriptionPosition="above"
            placeholder="Enter text"
            name={'approverNotes'}
            control={control}
            rules={{
              maxLength: { value: 255, message: 'Maximum allowed characters is 255' },
            }}
            errorLeadingIconName={'error'}
          />

          {/* Paid break exceptions error */}
          {paidBreakExceptionIsEnabled && formState.errors.paidBreakExceptions && (
            <FormFieldSupportingText
              id="paid-break-exceptions-error"
              isError
              leadingIconName="error"
              role="alert"
              ariaLive="polite"
              data-paid-break-error
            >
              {formState.errors.paidBreakExceptions.message}
            </FormFieldSupportingText>
          )}

          {isSubmissionError && (
            <div className="text-14 text-error flex gap-4">
              <MaterialIcon iconName={'error'} size={'sm'} />
              <p>
                Sorry, unable to process timesheet approval. Please try again later or contact{' '}
                <Link
                  href={`mailto:${appConfig.feedback.email}`}
                  className="text-primary hover:text-primary-shade focus:text-primary-shade hover:underline"
                >
                  {appConfig.feedback.email}
                </Link>{' '}
                if the issue persists.
              </p>
            </div>
          )}

          <div className="pt-8">
            <div ref={buttonAnchorRef} />
            <div
              className={
                isButtonFixed
                  ? 'max-tablet-portrait:fixed bottom-0 right-0 left-0 bg-surface-l0 max-tablet-portrait:p-16'
                  : ''
              }
            >
              <Button type="submit" className={'w-full'} disabled={isSubmissionPending} isLoading={isSubmissionPending}>
                Approve timesheet
              </Button>
            </div>
          </div>
        </div>
      </form>
    </Card>
  );
}
